import Phaser from 'phaser';
import { assetBundleManager } from '@/game/assets/AssetBundleManager';
import { eventBus } from '@/shared/EventBus';
import { useGameStore } from '@/ui/store/gameStore';
import { startTeleport, biomeAccent } from '@/game/fx/teleport';
import { bossApi, ApiError } from '@/net/api';
import type { BossHitResponse, BossView } from '@/net/api';
import { runEconomyAction, ECONOMY_RESYNC_CODE } from '@/net/economyActions';
import { saveManager } from '@/save';
import { PETS_BY_ID } from '@/config/pets.config';
import { PET_SLOTS } from '@/config/balance.config';
import { Player } from '@/game/entities/Player';
import { Pet } from '@/game/entities/Pet';
import { spawnSparkleBurst } from '@/game/fx/variantFx';
import { spawnDestructionFX } from '@/game/fx/CoinBurst';
import { InputSystem } from '@/game/systems/InputSystem';
import { TargetingSystem } from '@/game/systems/TargetingSystem';
import { ArenaTarget } from '@/game/entities/PetTarget';
import type { PetTarget } from '@/game/entities/PetTarget';
import type { PetInstance } from '@/domain/types/pet';

// ── Stałe walki (ATL-2b.B) ───────────────────────────────────────────────────
/** Cadencja uderzeń klienta. Serwer i tak clampuje per-tick do budżetu czasowego. */
const HIT_INTERVAL_MS = 700;
/**
 * Damage raportowany w każdym ticku. Serwer liczy budżet autorytatywnie
 * (`applied = min(damage, elapsed×dps_cap, capPerCall)`) — klient NIE zna krzywej,
 * więc raportuje stałą dużą wartość, a serwer przycina do budżetu. 10^18 ≤ 25 cyfr (schema).
 */
const REPORTED_DAMAGE = '1000000000000000000';

/** Posągi-pomocnicy spawnowane na fazie (wizualizacja serwerowej puli HP tarczy). */
const HELPER_STATUES = 3;

/** Cooldown „DO BOJU!" — guard sceny + lustro w UI (przez `sanctum:rallyState`). */
const RALLY_COOLDOWN_MS = 3_000;

/**
 * Taunty kontekstowe (PNG-free dymki nad bossem). Fazowe indeksowane numerem fazy
 * (1-based z serwera); shieldDrop losowany. Statyczne — bez zależności od configu.
 */
const TAUNTS = {
  awaken: 'Nie przejdziesz, śmiertelniku!',
  phase: ['Strażnicy, powstańcie!', 'Posągi, brońcie świątyni!'],
  shieldDrop: ['Niemożliwe… moja tarcza!', 'Jak śmiecie tknąć kolosa!'],
  defeat: 'Strzeż… Atlantydy…',
};

/** Faza klienta areny. */
type CombatPhase = 'intro' | 'cooling' | 'fighting' | 'defeated' | 'pending' | 'error';

/**
 * AtlantisSanctumScene — dedykowana arena World Bossa „Strażnik Świątyni" (ATL-2b).
 *
 * Wzorzec sub-sceny jak `PlazaScene`: launchowana przez `TeleportScene` (cel
 * 'AtlantisSanctumScene'), wyjście przez `sanctum:exit` (React → teleport do
 * WorldScene).
 *
 * - ATL-2b.A: OPRAWA — tło areny + uśpiona statua → BUDZI się (wir + idle A/B).
 * - ATL-2b.B/C: pętla `/boss/hit` (serwerowe HP), pasek HP, fazy/tarcza, posągi.
 * - Rework (pełna walka): arena reużywa silnika świata — PRAWDZIWY gracz
 *   (sterowalny joystickiem/WASD) + pety FSM z equipped + `TargetingSystem`.
 *   Kolos i posągi to NIEAUTORYTATYWNE `ArenaTarget` (pety biją je dla efektu:
 *   bieganie, lunge, iskry, dymki obrażeń, warianty), a HP/tarcza/nagroda są
 *   liczone WYŁĄCZNIE serwerowo przez pętlę `/boss/hit`. Tarcza = boss
 *   `setTargetable(false)` → `TargetingSystem` SAM przerzuca pety na posągi.
 *
 * Server-authoritative: HP, budżet obrażeń, roll nagrody (pod lockiem + audyt).
 * Klient tylko raportuje i renderuje stan z odpowiedzi.
 *
 * Assety (PreloadScene, guard enabled biom): `bg_atlantis_sanctum`,
 * `boss_atlantis_guardian` (statua 512), sheety `boss_atlantis_awake_a/b` +
 * `fx_atlantis_boss_swirl_sheet` (5×5, 25 klatek), `fx_atlantis_boss_swirl` (fallback).
 */
export class AtlantisSanctumScene extends Phaser.Scene {
  private floor?: Phaser.GameObjects.TileSprite;
  private boss?: Phaser.GameObjects.Sprite;
  private bossSize = 320;
  private offExit?: () => void;
  private offRally?: () => void;
  private rallyReadyAt = 0; // guard cooldownu „DO BOJU!" (czas sceny)

  // ── Walka (ATL-2b.B) ────────────────────────────────────────────────────
  private combatPhase: CombatPhase = 'intro';
  private hitTimer?: Phaser.Time.TimerEvent;
  private coolingTimer?: Phaser.Time.TimerEvent;
  private bossActionTimer?: Phaser.Time.TimerEvent; // laser/fala bossa (dynamika)
  private fightSyncTimer?: Phaser.Time.TimerEvent; // heartbeat stanu walki (MED review)
  private fightSyncInFlight = false;
  private hitInFlight = false;
  /** Guard async-callbacków rozwiązujących się po SHUTDOWN sceny. */
  private dead = false;

  // Serwerowe HP (BigInt — bywa > 2^53).
  private hpMax = 0n;
  private hpRemaining = 0n;
  private shielded = false;
  /** Koniec okna walki (serwerowy epoch ms) → licznik napięcia. null = brak. */
  private awakenedUntil: number | null = null;

  // Fazy / tarcza — posągi-pomocnicy jako prawdziwe cele petów (ArenaTarget).
  private helperStatues: { sprite: Phaser.GameObjects.Image; target: ArenaTarget }[] = [];
  private bossAura?: Phaser.GameObjects.Arc;
  /** Trwałe dekoracje kina śmierci (pęknięcia/promienie) — gaszone przy rozpadzie. */
  private finaleFx: Phaser.GameObjects.GameObject[] = [];
  /** Dramatyczne elementy portalu przebudzenia (dysk/pierścień) — gaszone po wejściu. */
  private bossPortal: Phaser.GameObjects.GameObject[] = [];
  /** Trwały wir za bossem (zostaje na czas walki) — gaszony przy pokonaniu. */
  private bossSwirl?: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image;
  /** Pulsująca świetlista aura obudzonego kolosa (trwała) — gaszona przy pokonaniu. */
  private bossHalo?: Phaser.GameObjects.Arc;
  private phaseHpMax = 0n;
  private phaseHpRemaining = 0n;

  // ── Prawdziwa walka (rework): gracz + pety FSM + system celowania ────────
  // Arena reużywa tej samej maszynerii co świat. Boss/posągi to NIEAUTORYTATYWNE
  // `ArenaTarget` (HP/tarcza liczone serwerowo) — pety biją je dla efektu.
  private inputSystem?: InputSystem;
  private player?: Player;
  private pets: Pet[] = [];
  private targeting?: TargetingSystem;
  /** Kotwice ataku wokół podstawy kolosa (1 slot każda) → pety rozkładają się
   *  w wachlarz zamiast tłoczyć w jednym punkcie. */
  private bossAnchors: ArenaTarget[] = [];
  /** Cele dla TargetingSystem (kotwice bossa + posągi). Mutowane in-place (TS trzyma ref). */
  private targets: PetTarget[] = [];

  // HUD bossa.
  private hpBarBg?: Phaser.GameObjects.Rectangle;
  private hpBarFill?: Phaser.GameObjects.Rectangle;
  private hpNameLabel?: Phaser.GameObjects.Text;
  private hpPctLabel?: Phaser.GameObjects.Text;
  private shieldLabel?: Phaser.GameObjects.Text;
  private statusLabel?: Phaser.GameObjects.Text;
  private windowLabel?: Phaser.GameObjects.Text; // licznik okna walki „Czas: M:SS"
  private windowShownSec = -1; // ostatnio wyrenderowana sekunda (anty-churn)
  private cooldownUntil: number | null = null;

  constructor() {
    super({ key: 'AtlantisSanctumScene' });
  }

  create(): void {
    assetBundleManager.activateScene(
      this.textures,
      this.anims,
      'scene:sanctum',
      saveManager.getCurrent(),
    );
    useGameStore.getState().setActiveScene('sanctum');
    this.cameras.main.setBackgroundColor('#0c3a44'); // głębia, pod tłem

    // Sceny Phasera to singletony — pola klasy przeżywają restart (TeleportScene
    // robi stop→start). Reset stanu walki, by każde wejście było świeże.
    this.resetCombatState();

    const { width: w, height: h } = this.scale;

    // ── Podłoga areny — seamless tile (bg_atlantis_sanctum 512). Fallback: kolor. ─
    if (this.textures.exists('bg_atlantis_sanctum')) {
      this.floor = this.add.tileSprite(0, 0, w, h, 'bg_atlantis_sanctum').setOrigin(0, 0).setDepth(0);
      this.floor.setTileScale(0.9);
    }

    // ── Animacje bossa/wiru (globalne, idempotentne) ────────────────────────
    this.ensureBossAnims();

    // ── Kolos — najpierw uśpiona statua (512). setDisplaySize ujednolica skalę
    //    między statuą (512) a klatkami awake (256). ──────────────────────────
    this.bossSize = Math.min(w, h) * 0.52;
    const dormantKey = this.textures.exists('boss_atlantis_guardian')
      ? 'boss_atlantis_guardian'
      : 'placeholder-square';
    this.boss = this.add
      .sprite(w / 2, h * 0.58, dormantKey)
      .setOrigin(0.5, 0.85)
      .setDepth(10)
      .setDisplaySize(this.bossSize, this.bossSize);
    // Lekki „oddech" uśpionej statuy do czasu przebudzenia.
    this.tweens.add({
      targets: this.boss,
      scaleX: { from: this.boss.scaleX, to: this.boss.scaleX * 1.015 },
      scaleY: { from: this.boss.scaleY, to: this.boss.scaleY * 1.015 },
      duration: 1800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // ── Stan bossa z serwera → decyzja: walka / cooldown / pending ───────────
    // (Zastępuje bezwarunkowe przebudzenie z ATL-2b.A — kolos budzi się tylko
    //  gdy serwer mówi, że jest gotów do walki.)
    void this.beginCombat();

    // ── Wyjście (React „Opuść Sanktuarium" → event) ─────────────────────────
    this.offExit = eventBus.on('sanctum:exit', () => this.exitToWorld());
    // ── „DO BOJU!" (React) → wizualny zryw drużyny (nie zmienia serwerowego HP) ─
    this.offRally = eventBus.on('sanctum:rally', () => this.rally());

    this.scale.on('resize', this.layout, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.dead = true;
      this.tweens.timeScale = 1; // reset slow-mo finału, gdyby wyjście trafiło w trakcie
      // ⚠ NIE wołaj tu `this.cameras.main.setZoom(1)` — przy SHUTDOWN (np. podmiana
      // scen w teleporcie) `cameras.main` bywa już undefined → TypeError w handlerze
      // SHUTDOWN PRZERYWAŁ podmianę scen w `TeleportScene.performSwap` (świat nie
      // wstawał → zawieszony portal/soft-lock powrotu). Reset zoomu kin robi
      // `resetCombatState()` przy KAŻDYM wejściu — to wystarcza (kamera i tak ginie z sceną).
      this.stopHitLoop();
      this.stopBossActions();
      this.coolingTimer?.remove();
      this.coolingTimer = undefined;
      this.targeting?.stop();
      this.inputSystem?.detach();
      for (const pet of this.pets) pet.destroy(); // tweeny/aury wariantów
      this.pets = [];
      this.scale.off('resize', this.layout, this);
      this.offExit?.();
      this.offExit = undefined;
      this.offRally?.();
      this.offRally = undefined;
    });
  }

  /** Reposycjonowanie przy zmianie rozmiaru okna (Phaser Scale.RESIZE). */
  private layout(gameSize: Phaser.Structs.Size): void {
    const w = gameSize.width;
    const h = gameSize.height;
    this.floor?.setSize(w, h);
    if (this.boss) {
      this.bossSize = Math.min(w, h) * 0.52;
      this.boss.setPosition(w / 2, h * 0.58).setDisplaySize(this.bossSize, this.bossSize);
    }
    // HUD bossa — najprościej przebudować (resize jest rzadki).
    if (this.hpBarBg) {
      this.buildHpBar();
      this.updateHpBar();
    }
    this.statusLabel?.setPosition(w / 2, h * 0.5);
    if (this.bossAura && this.boss) {
      this.bossAura.setPosition(this.boss.x, this.boss.y - this.bossSize * 0.3).setRadius(this.bossSize * 0.6);
    }
    if (this.helperStatues.length > 0) this.layoutHelperStatues();
    // Gracz nie wychodzi poza widoczną arenę.
    this.physics.world.setBounds(0, 0, w, h);
  }

  /** Per-frame tick gracza + petów FSM (Phaser woła to automatycznie). */
  update(time: number, deltaMs: number): void {
    if (this.dead) return;
    const dt = deltaMs / 1000;
    this.player?.update(time, dt);
    for (const pet of this.pets) pet.update(dt, time);
    this.updateWindowLabel();
  }

  /** Licznik okna walki „Czas: M:SS" — napięcie. Czerwienieje + pulsuje <60 s. */
  private updateWindowLabel(): void {
    const label = this.windowLabel;
    if (!label) return;
    if (this.combatPhase !== 'fighting' || this.awakenedUntil == null) {
      if (label.visible) label.setVisible(false);
      return;
    }
    const sec = Math.max(0, Math.ceil((this.awakenedUntil - Date.now()) / 1000));
    if (sec === this.windowShownSec) {
      if (!label.visible) label.setVisible(true);
      return;
    }
    this.windowShownSec = sec;
    const mm = Math.floor(sec / 60);
    const ss = (sec % 60).toString().padStart(2, '0');
    label.setText(`Czas: ${mm}:${ss}`);
    const urgent = sec <= 60;
    label.setColor(urgent ? '#ff6b5e' : '#f4ecd8').setVisible(true);
    if (urgent) {
      label.setScale(1.14);
      this.tweens.add({ targets: label, scale: 1, duration: 320, ease: 'Quad.easeOut' });
    }
  }

  /** Sekwencja przebudzenia: wir (one-shot) + wstrząs kamery + pętla idle awake. */
  private awaken(): void {
    if (!this.boss || !this.boss.active) return;
    this.tweens.killTweensOf(this.boss); // zatrzymaj „oddech" statuy

    // Wir przebudzenia — animowany sheet (one-shot), za bossem, ADD glow.
    if (this.textures.exists('fx_atlantis_boss_swirl_sheet') && this.anims.exists('fx_atlantis_boss_swirl_anim')) {
      const swirl = this.add
        .sprite(this.boss.x, this.boss.y - this.bossSize * 0.35, 'fx_atlantis_boss_swirl_sheet')
        .setDepth(8)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDisplaySize(this.bossSize * 1.6, this.bossSize * 1.6)
        .setAlpha(0.9);
      swirl.play('fx_atlantis_boss_swirl_anim');
      swirl.once(Phaser.Animations.Events.ANIMATION_COMPLETE, () => swirl.destroy());
    } else if (this.textures.exists('fx_atlantis_boss_swirl')) {
      // Fallback statyczny — krótki błysk.
      const swirl = this.add
        .image(this.boss.x, this.boss.y - this.bossSize * 0.35, 'fx_atlantis_boss_swirl')
        .setDepth(8)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDisplaySize(this.bossSize * 1.4, this.bossSize * 1.4);
      this.tweens.add({ targets: swirl, alpha: 0, scale: swirl.scale * 1.3, duration: 700, onComplete: () => swirl.destroy() });
    }

    this.cameras.main.shake(420, 0.012);
    this.playAwakeIdle();
  }

  /** Pętla idle awake: sheet A → (complete) → B → A… (na przemian, jak NPC plazy). */
  private playAwakeIdle(): void {
    const spr = this.boss;
    if (!spr || !this.anims.exists('boss_atlantis_awake_a_anim')) return;
    let useA = true;
    const loop = (): void => {
      if (!spr.active) return;
      spr.play(useA ? 'boss_atlantis_awake_a_anim' : 'boss_atlantis_awake_b_anim');
      // Frame awake = 256 (statua była 512) → ponownie ujednolić rozmiar wyświetlania.
      spr.setDisplaySize(this.bossSize, this.bossSize);
      useA = !useA;
    };
    spr.on(Phaser.Animations.Events.ANIMATION_COMPLETE, loop);
    loop();
  }

  /** Tworzy globalne animacje bossa/wiru z sheetów 5×5 (klatki 0..24). Idempotentne. */
  private ensureBossAnims(): void {
    const mk = (key: string, sheet: string, frameRate: number, repeat: number): void => {
      if (this.anims.exists(key) || !this.textures.exists(sheet)) return;
      this.anims.create({
        key,
        frames: this.anims.generateFrameNumbers(sheet, { start: 0, end: 24 }),
        frameRate,
        repeat,
      });
    };
    mk('boss_atlantis_awake_a_anim', 'boss_atlantis_awake_a', 10, 0);
    mk('boss_atlantis_awake_b_anim', 'boss_atlantis_awake_b', 10, 0);
    mk('fx_atlantis_boss_swirl_anim', 'fx_atlantis_boss_swirl_sheet', 18, 0);
    mk('fx_atlantis_boss_swirl_loop', 'fx_atlantis_boss_swirl_sheet', 16, -1); // trwały wir portalu
  }

  /**
   * Kino przebudzenia (~6,8 s) — wolne, widowiskowe wejście Strażnika z PORTALEM
   * i trwałym wirem za nim. Pety stoją (boss nietykalny) aż do `done()`. Fazy:
   *   A (0–2,9 s) portal się otwiera + wsysane światełka,
   *   B (2,9–4,3 s) pękająca skorupa + fale promieni, wir się wzmaga,
   *   C (4,3 s) POWSTAJE — błysk + słup światła + animacja awake + scale-punch,
   *   D (5,5–6,8 s) wyciszenie kamery, zostaje subtelny wir za bossem → `done()`.
   */
  private playAwakenCinematic(done: () => void): void {
    const cam = this.cameras.main;
    const b = this.boss;
    if (b) this.tweens.killTweensOf(b); // stop „oddechu" uśpionej statuy

    // ── FAZA A: portal się otwiera ────────────────────────────────────────
    this.finaleBanner('Coś budzi się w głębi...');
    cam.zoomTo(1.16, 2800, 'Sine.easeInOut');
    this.spawnAwakenPortal();
    this.spawnDriftMotes(20, 5200); // świecące drobinki dryfujące w górę przez całe kino
    for (let i = 0; i < 4; i++) {
      this.beat(500 + i * 560, () => cam.shake(220, 0.006 + i * 0.002));
    }

    // ── FAZA B: pękająca skorupa + promienie ──────────────────────────────
    this.beat(2900, () => {
      this.finaleBanner('STRAŻNIK ŚWIĄTYNI POWSTAJE!');
      this.spawnCracks();
      this.intensifyPortal();
      cam.zoomTo(1.26, 1400, 'Sine.easeInOut');
    });
    this.beat(3200, () => {
      this.radiateBeams();
      cam.shake(360, 0.014);
    });
    this.beat(3800, () => {
      this.radiateBeams();
      cam.shake(360, 0.016);
    });

    // ── FAZA C: POWSTAJE ──────────────────────────────────────────────────
    this.beat(4300, () => {
      cam.flash(700, 210, 245, 255);
      this.lightPillar();
      cam.shake(700, 0.02);
      this.fadeFinaleFx(); // zgaś pęknięcia — boss przechodzi w tryb awake
      if (b) {
        this.spawnStarburstFlare(b.x, b.y - this.bossSize * 0.4); // rozbłysk-gwiazda
        this.playAwakeIdle();
        this.tweens.add({
          targets: b,
          scaleX: b.scaleX * 1.12,
          scaleY: b.scaleY * 1.12,
          duration: 280,
          yoyo: true,
          ease: 'Sine.easeInOut',
        });
      }
      this.spawnBossHalo(); // trwała świetlista aura na czas walki
      this.showTaunt(TAUNTS.awaken);
    });

    // ── FAZA D: wyciszenie + zostaw subtelny wir za bossem ────────────────
    this.beat(5500, () => {
      cam.zoomTo(1, 1400, 'Sine.easeInOut');
      this.settlePortal();
    });
    this.beat(6800, () => done());
  }

  /** Portal przebudzenia za bossem: dysk + pierścień + trwały wir + wsysane motki. */
  private spawnAwakenPortal(): void {
    const b = this.boss;
    if (!b) return;
    const cx = b.x;
    const cy = b.y - this.bossSize * 0.42;

    const disc = this.add
      .circle(cx, cy, this.bossSize * 0.5, 0x0a3b44, 0.55)
      .setDepth(6)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0)
      .setScale(0.1);
    const ring = this.add
      .circle(cx, cy, this.bossSize * 0.5, 0x000000, 0)
      .setStrokeStyle(6, 0x6fe3f0, 0.9)
      .setDepth(6)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0)
      .setScale(0.1);
    this.bossPortal.push(disc, ring);
    this.tweens.add({ targets: disc, scale: 1, alpha: 0.6, duration: 2600, ease: 'Sine.easeOut' });
    this.tweens.add({ targets: ring, scale: 1.15, alpha: 0.9, duration: 2600, ease: 'Sine.easeOut' });

    // Trwały wir (loop) — zostaje za bossem na czas walki.
    let swirl: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image | undefined;
    if (
      this.textures.exists('fx_atlantis_boss_swirl_sheet') &&
      this.anims.exists('fx_atlantis_boss_swirl_loop')
    ) {
      const sp = this.add
        .sprite(cx, cy, 'fx_atlantis_boss_swirl_sheet')
        .setDepth(7)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDisplaySize(this.bossSize * 0.3, this.bossSize * 0.3)
        .setAlpha(0);
      sp.play('fx_atlantis_boss_swirl_loop');
      swirl = sp;
    } else if (this.textures.exists('fx_atlantis_boss_swirl')) {
      const img = this.add
        .image(cx, cy, 'fx_atlantis_boss_swirl')
        .setDepth(7)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDisplaySize(this.bossSize * 0.3, this.bossSize * 0.3)
        .setAlpha(0);
      this.tweens.add({ targets: img, angle: 360, duration: 9000, repeat: -1, ease: 'Linear' });
      swirl = img;
    }
    if (swirl) {
      this.bossSwirl = swirl;
      this.tweens.add({
        targets: swirl,
        scaleX: swirl.scaleX * 5.3, // 0.3× → ~1.6× bossSize
        scaleY: swirl.scaleY * 5.3,
        alpha: 0.85,
        duration: 2600,
        ease: 'Sine.easeOut',
      });
    }
    this.spawnPortalGodRays(cx, cy);
    this.portalMotes(cx, cy);
  }

  /** Małe światełka wsysane do otwierającego się portalu. */
  private portalMotes(cx: number, cy: number): void {
    for (let i = 0; i < 14; i++) {
      this.beat(i * 170, () => {
        const ang = Phaser.Math.FloatBetween(0, Math.PI * 2);
        const r = this.bossSize * Phaser.Math.FloatBetween(0.6, 1.0);
        const mote = this.add
          .circle(cx + Math.cos(ang) * r, cy + Math.sin(ang) * r, Phaser.Math.Between(2, 5), 0x9ff0ff, 0.9)
          .setDepth(7)
          .setBlendMode(Phaser.BlendModes.ADD);
        this.tweens.add({
          targets: mote,
          x: cx,
          y: cy,
          alpha: 0.2,
          scale: 0.4,
          duration: 720,
          ease: 'Quad.easeIn',
          onComplete: () => mote.destroy(),
        });
      });
    }
  }

  /** Promienie boskiego światła z portalu (miękkie, wolno wirują; klimat Atlantydy). */
  private spawnPortalGodRays(cx: number, cy: number): void {
    const container = this.add.container(cx, cy).setDepth(7);
    this.bossPortal.push(container);
    const N = 8;
    for (let i = 0; i < N; i++) {
      const ray = this.add
        .triangle(
          0,
          0,
          0,
          0,
          -this.bossSize * 0.18,
          -this.bossSize * 1.45,
          this.bossSize * 0.18,
          -this.bossSize * 1.45,
          0xbff0ff,
          0.1,
        )
        .setBlendMode(Phaser.BlendModes.ADD);
      ray.setAngle((i / N) * 360);
      container.add(ray);
    }
    container.setScale(0.2).setAlpha(0);
    this.tweens.add({ targets: container, scale: 1, alpha: 1, duration: 2600, ease: 'Sine.easeOut' });
    this.tweens.add({ targets: container, angle: 360, duration: 16000, repeat: -1, ease: 'Linear' });
  }

  /** Dryfujące świecące drobinki unoszące się wokół kolosa (ambient kina). */
  private spawnDriftMotes(count: number, spreadMs: number): void {
    const b = this.boss;
    if (!b) return;
    for (let i = 0; i < count; i++) {
      this.beat(Phaser.Math.Between(0, spreadMs), () => {
        const bx = this.boss?.x ?? b.x;
        const by = this.boss?.y ?? b.y;
        const x = bx + Phaser.Math.Between(-this.bossSize * 0.5, this.bossSize * 0.5);
        const y = by + Phaser.Math.Between(-this.bossSize * 0.1, this.bossSize * 0.05);
        const mote = this.add
          .circle(x, y, Phaser.Math.Between(2, 4), 0xcfeaff, 0.85)
          .setDepth(7)
          .setBlendMode(Phaser.BlendModes.ADD);
        this.tweens.add({
          targets: mote,
          y: y - this.bossSize * Phaser.Math.FloatBetween(0.4, 0.8),
          x: x + Phaser.Math.Between(-20, 20),
          alpha: 0,
          duration: Phaser.Math.Between(1400, 2400),
          ease: 'Sine.easeOut',
          onComplete: () => mote.destroy(),
        });
      });
    }
  }

  /** Rozbłysk-gwiazda (spokes + bloom) w punkcie — kulminacja „POWSTAJE". */
  private spawnStarburstFlare(cx: number, cy: number): void {
    const spokes = 6;
    for (let i = 0; i < spokes; i++) {
      const line = this.add
        .rectangle(cx, cy, this.bossSize * 1.8, 4, 0xffffff, 0.9)
        .setDepth(20)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setRotation((i / spokes) * Math.PI)
        .setScale(0.1, 1);
      this.tweens.add({
        targets: line,
        scaleX: 1.5,
        alpha: 0,
        duration: 650,
        ease: 'Quad.easeOut',
        onComplete: () => line.destroy(),
      });
    }
    const bloom = this.add
      .circle(cx, cy, this.bossSize * 0.2, 0xffffff, 0.9)
      .setDepth(20)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: bloom,
      scale: 3,
      alpha: 0,
      duration: 720,
      ease: 'Quad.easeOut',
      onComplete: () => bloom.destroy(),
    });
  }

  /** Trwała pulsująca świetlista aura obudzonego kolosa (zostaje na czas walki). */
  private spawnBossHalo(): void {
    const b = this.boss;
    if (!b) return;
    this.bossHalo?.destroy();
    this.bossHalo = this.add
      .circle(b.x, b.y - this.bossSize * 0.35, this.bossSize * 0.55, 0x8fe8ff, 0.22)
      .setDepth(7)
      .setBlendMode(Phaser.BlendModes.ADD)
      .setAlpha(0);
    this.tweens.add({
      targets: this.bossHalo,
      alpha: 0.24,
      scale: 1.06,
      duration: 1400,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  /** Wzmocnienie wiru w fazie B (większy, jaśniejszy). */
  private intensifyPortal(): void {
    if (!this.bossSwirl) return;
    this.tweens.add({
      targets: this.bossSwirl,
      scaleX: this.bossSwirl.scaleX * 1.3,
      scaleY: this.bossSwirl.scaleY * 1.3,
      alpha: 1,
      duration: 1200,
      ease: 'Sine.easeOut',
    });
  }

  /** Po przebudzeniu: zgaś dysk/pierścień, zostaw subtelny wir za bossem. */
  private settlePortal(): void {
    const drama = this.bossPortal;
    this.bossPortal = [];
    for (const o of drama) {
      const go = o as Phaser.GameObjects.GameObject & { destroy: () => void };
      this.tweens.killTweensOf(o);
      this.tweens.add({ targets: o, alpha: 0, duration: 900, onComplete: () => go.destroy() });
    }
    if (this.bossSwirl) {
      this.tweens.add({
        targets: this.bossSwirl,
        scaleX: this.bossSwirl.scaleX * 0.62,
        scaleY: this.bossSwirl.scaleY * 0.62,
        alpha: 0.32,
        duration: 1300,
        ease: 'Sine.easeInOut',
      });
    }
  }

  /** Zgaś portal + trwały wir (np. przy pokonaniu / sprzątaniu). */
  private clearBossPortal(): void {
    const all = [...this.bossPortal];
    this.bossPortal = [];
    if (this.bossSwirl) {
      all.push(this.bossSwirl);
      this.bossSwirl = undefined;
    }
    if (this.bossHalo) {
      all.push(this.bossHalo);
      this.bossHalo = undefined;
    }
    for (const o of all) {
      const go = o as Phaser.GameObjects.GameObject & { destroy: () => void };
      this.tweens.killTweensOf(o);
      this.tweens.add({ targets: o, alpha: 0, duration: 500, onComplete: () => go.destroy() });
    }
  }

  // ══ ATL-2b.B: pętla walki ════════════════════════════════════════════════

  /** Reset pól walki (scena to singleton — pola przeżywają restart). */
  private resetCombatState(): void {
    this.dead = false;
    this.tweens.timeScale = 1; // defensywnie: poprzednie życie mogło zostawić slow-mo
    this.cameras.main.setZoom(1); // i zoom finału (gdyby wyjście trafiło w środek kina)
    this.finaleFx = []; // obiekty z poprzedniego życia ubite w SHUTDOWN — czyść referencje
    this.bossPortal = [];
    this.bossSwirl = undefined;
    this.bossHalo = undefined;
    this.combatPhase = 'intro';
    this.hitInFlight = false;
    this.hpMax = 0n;
    this.hpRemaining = 0n;
    this.shielded = false;
    this.awakenedUntil = null;
    this.windowShownSec = -1;
    this.phaseHpMax = 0n;
    this.phaseHpRemaining = 0n;
    this.helperStatues = [];
    this.bossAura = undefined;
    this.cooldownUntil = null;
    // GameObjects z poprzedniego życia sceny zniszczone przez SHUTDOWN —
    // czyścimy tylko referencje, by guardy `if (!this.hpBarBg)` działały.
    this.hpBarBg = undefined;
    this.hpBarFill = undefined;
    this.hpNameLabel = undefined;
    this.hpPctLabel = undefined;
    this.shieldLabel = undefined;
    this.statusLabel = undefined;
    this.windowLabel = undefined;
    this.hitTimer = undefined;
    this.coolingTimer = undefined;
    this.bossActionTimer = undefined;
    this.fightSyncTimer = undefined;
    this.fightSyncInFlight = false;
    // Walka (gracz/pety/celowanie) — referencje, obiekty ubite w SHUTDOWN.
    this.pets = [];
    this.targets = [];
    this.player = undefined;
    this.inputSystem = undefined;
    this.targeting = undefined;
    this.bossAnchors = [];
    this.rallyReadyAt = 0;
  }

  /** Pobiera stan bossa i rozgałęzia: walka / cooldown / nieodebrana nagroda. */
  private async beginCombat(): Promise<void> {
    let view: BossView;
    try {
      view = await bossApi.getState();
    } catch {
      if (this.dead) return;
      this.combatPhase = 'error';
      this.showStatus('Nie udało się obudzić Strażnika.\nWyjdź i wejdź ponownie.');
      return;
    }
    if (this.dead) return;

    // Nieodebrana nagroda z poprzedniej walki (inventory było pełne) — priorytet.
    if (view.pendingReward) {
      this.combatPhase = 'pending';
      void this.tryClaimPending(true);
      return;
    }

    // Boss śpi (cooldown) — pokaż licznik, NIE budź.
    if (view.state === 'dormant_cooling') {
      this.combatPhase = 'cooling';
      this.showCooling(view.nextAvailableAt);
      return;
    }

    // Gotów (ready) lub już obudzony (relog w trakcie walki) → WALKA.
    this.combatPhase = 'fighting';
    if (view.hpMax !== '0') {
      this.hpMax = BigInt(view.hpMax);
      this.hpRemaining = BigInt(view.hpRemaining);
      this.shielded = view.shielded;
      this.phaseHpRemaining = BigInt(view.phaseHpRemaining);
    }
    this.awakenedUntil = view.awakenedUntil;
    // Relog w trakcie już-obudzonej walki → bez długiego kina (boss już stoi).
    const isRelog = view.state === 'awake';
    // Krótki telegraf, potem kino przebudzenia (lub szybkie wejście przy relogu).
    this.time.delayedCall(650, () => {
      if (this.dead || this.combatPhase !== 'fighting') return;
      this.setupCombatants();
      if (this.pets.length === 0) {
        // Brak wyekwipowanych petów (LOW review): boss budzi się na pokaz, ale walki nie ma.
        // Komunikat zamiast spamu /boss/hit co 700 ms bez atakujących petów.
        this.combatPhase = 'error';
        this.showStatus('Załóż pety w ekwipunku,\nby walczyć ze Strażnikiem.');
        return;
      }
      // Pety NIE biją, póki kolos się nie obudzi (kino wejścia gra na nietykalnym bossie).
      this.setBossTargetable(false);

      const startFight = (): void => {
        if (this.dead || this.combatPhase !== 'fighting') return;
        this.buildHpBar();
        if (this.hpMax > 0n) this.updateHpBar();
        this.setBossTargetable(!this.shielded);
        this.showTaunt(TAUNTS.awaken);
        this.startHitLoop();
        this.startBossActions();
        // Relog w trakcie fazy tarczy → odtwórz posągi (pełna pula nieznana po
        // reloginie → bieżąca wartość jako 100% paska tarczy).
        if (this.shielded) {
          this.phaseHpMax = this.phaseHpRemaining > 0n ? this.phaseHpRemaining : 1n;
          this.enterShieldVisuals();
        }
      };

      if (isRelog) {
        this.awaken(); // szybkie wejście (relog) — boss już był obudzony
        startFight();
      } else {
        this.playAwakenCinematic(startFight); // pełne kino wejścia + portal
      }
    });
  }

  /**
   * Stawia prawdziwych zawodników areny: gracz (sterowalny joystickiem/WASD),
   * pety FSM z equipped, cel-kolos i system celowania. Pety same biegają,
   * atakują z efektami i przełączają się na posągi (tarcza) — jak w świecie.
   */
  private setupCombatants(): void {
    const { width: w, height: h } = this.scale;
    this.physics.world.setBounds(0, 0, w, h); // gracz zostaje w widoku areny

    this.inputSystem = new InputSystem();
    this.inputSystem.attach(this);
    const skinId = saveManager.getCurrent().profile.avatar.skinId;
    this.player = new Player(this, w / 2, h * 0.86, this.inputSystem, skinId);

    // Cel-kolos (nieautorytatywny): wachlarz kotwic u podstawy bossa (po 1 slocie)
    // → pety rozkładają się przed kolosem, nie tłoczą w jednym punkcie. HP/tarcza
    // liczy serwer; osłonięty tarczą = wszystkie kotwice nietykalne.
    this.targets.length = 0;
    this.bossAnchors = [];
    const anchorCount = PET_SLOTS.maxCount;
    for (let i = 0; i < anchorCount; i++) {
      const t = anchorCount > 1 ? i / (anchorCount - 1) : 0.5; // 0..1 w poprzek
      const anchor = new ArenaTarget(() => {
        const bx = this.boss?.x ?? w / 2;
        const by = this.boss?.y ?? h * 0.58;
        return {
          x: bx + (t - 0.5) * this.bossSize * 0.62,
          y: by + this.bossSize * 0.04 + Math.sin(t * Math.PI) * this.bossSize * 0.05,
        };
      }, 1);
      anchor.setTargetable(!this.shielded);
      this.bossAnchors.push(anchor);
      this.targets.push(anchor);
    }

    this.spawnEquippedPets();

    this.targeting = new TargetingSystem(this, this.player, this.pets, this.targets);
    this.targeting.start();
  }

  /** Spawnuje prawdziwe pety z `save.equipped` (jak WorldScene, mały wariant). */
  private spawnEquippedPets(): void {
    const save = saveManager.getCurrent();
    const equipped = save.equipped
      .map((uid) => save.inventory.find((p) => p.uid === uid))
      .filter((inst): inst is PetInstance => !!inst);
    const slotCount = equipped.length;
    this.pets = [];
    for (let i = 0; i < equipped.length; i++) {
      const inst = equipped[i]!;
      const def = PETS_BY_ID[inst.defId];
      if (!def || !this.player) continue;
      this.pets.push(new Pet(this, def, inst, i, slotCount, this.player));
    }
  }

  /** Buduje pasek HP bossa (orichalk) + etykiety. Idempotentne (przebudowa na resize). */
  private buildHpBar(): void {
    this.hpBarBg?.destroy();
    this.hpBarFill?.destroy();
    this.hpNameLabel?.destroy();
    this.hpPctLabel?.destroy();
    this.shieldLabel?.destroy();
    this.windowLabel?.destroy();

    const { width: w, height: h } = this.scale;
    const barW = Math.min(w * 0.62, 560);
    const barH = Math.max(14, h * 0.022);
    const x = w / 2 - barW / 2;
    const y = h * 0.085;

    this.hpNameLabel = this.add
      .text(w / 2, y - barH * 0.6, 'STRAŻNIK ŚWIĄTYNI', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(barH * 1.15)}px`,
        color: '#f7e9c8',
        fontStyle: 'bold',
        stroke: '#06141a',
        strokeThickness: Math.max(3, Math.round(barH * 0.3)),
      })
      .setOrigin(0.5, 1)
      .setDepth(101)
      .setScrollFactor(0)
      .setShadow(0, 2, 'rgba(0,0,0,0.7)', 5);

    this.hpBarBg = this.add
      .rectangle(x, y, barW, barH, 0x10222b, 0.92)
      .setOrigin(0, 0)
      .setDepth(100)
      .setStrokeStyle(2, 0xc8a24a, 0.9)
      .setScrollFactor(0);
    this.hpBarFill = this.add
      .rectangle(x + 2, y + 2, barW - 4, barH - 4, 0xe0843a, 1)
      .setOrigin(0, 0)
      .setDepth(101)
      .setScrollFactor(0);

    this.hpPctLabel = this.add
      .text(w / 2, y + barH / 2, '', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(barH * 0.8)}px`,
        color: '#0b1a20',
        fontStyle: 'bold',
      })
      .setOrigin(0.5, 0.5)
      .setDepth(102)
      .setScrollFactor(0);

    // Licznik okna walki — tuż pod paskiem (napięcie). Aktualizowany w update().
    this.windowLabel = this.add
      .text(w / 2, y + barH * 1.45, '', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(barH * 0.92)}px`,
        color: '#f4ecd8',
        fontStyle: 'bold',
        stroke: '#06141a',
        strokeThickness: Math.max(3, Math.round(barH * 0.26)),
      })
      .setOrigin(0.5, 0)
      .setDepth(102)
      .setScrollFactor(0)
      .setShadow(0, 2, 'rgba(0,0,0,0.7)', 4)
      .setVisible(false);

    this.shieldLabel = this.add
      .text(w / 2, y + barH * 2.7, 'TARCZA', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(barH * 0.9)}px`,
        color: '#7fe9f2',
        fontStyle: 'bold',
        stroke: '#06141a',
        strokeThickness: Math.max(3, Math.round(barH * 0.26)),
      })
      .setOrigin(0.5, 0)
      .setDepth(102)
      .setScrollFactor(0)
      .setShadow(0, 2, 'rgba(0,0,0,0.7)', 4)
      .setVisible(false);
  }

  /** Render paska: pula tarczy (cyjan) gdy shielded, inaczej HP bossa (orichalk). */
  private updateHpBar(): void {
    if (!this.hpBarBg || !this.hpBarFill) return;
    const innerW = this.hpBarBg.width - 4;
    const frac = this.barFraction();
    this.hpBarFill.width = Math.max(0, innerW * frac);
    this.hpBarFill.setFillStyle(this.shielded ? 0x4fd6e0 : 0xe0843a, 1);
    this.hpPctLabel?.setText(`${Math.round(frac * 100)}%`);
    this.shieldLabel?.setVisible(this.shielded);
  }

  /** Frakcja paska: pula tarczy gdy shielded, inaczej HP bossa. */
  private barFraction(): number {
    return this.shielded
      ? this.fractionOf(this.phaseHpRemaining, this.phaseHpMax)
      : this.fractionOf(this.hpRemaining, this.hpMax);
  }

  /** BigInt-safe frakcja remaining/max (wartości bywają > 2^53). */
  private fractionOf(remaining: bigint, max: bigint): number {
    if (max <= 0n) return 1;
    if (remaining <= 0n) return 0;
    const basis = 10000n;
    const frac = Number((remaining * basis) / max) / Number(basis);
    return Math.max(0, Math.min(1, frac));
  }

  /** Biały błysk kolosa = „dostał". Tani feedback per-tick. */
  private flashBossHit(): void {
    const boss = this.boss;
    if (!boss || !boss.active) return;
    boss.setTintFill(0xfff2d6);
    this.time.delayedCall(90, () => {
      if (boss.active) boss.clearTint();
    });
  }

  // ══ Dynamika: ataki kolosa (czysto wizualne — gra nie ma HP gracza) ══════════

  /** Startuje timery walki: cykliczne akcje kolosa (enrage) + heartbeat stanu (MED review). */
  private startBossActions(): void {
    this.stopBossActions();
    this.scheduleNextBossAction();
    // Heartbeat: gdy pety NIE atakują, `/boss/hit` jest pomijany (causal gating) i nie
    // wykryje wygaśnięcia okna walki po stronie serwera → scena utknęłaby w 'fighting'.
    // Niski-koszt GET tylko wtedy, gdy hit loop „głodzony". Autorytatywny (bez zegara klienta).
    this.fightSyncTimer = this.time.addEvent({
      delay: 4000,
      loop: true,
      callback: () => void this.syncFightState(),
    });
  }

  private stopBossActions(): void {
    this.bossActionTimer?.remove();
    this.bossActionTimer = undefined;
    this.fightSyncTimer?.remove();
    this.fightSyncTimer = undefined;
  }

  /** Heartbeat: gdy żaden pet nie atakuje, dopytaj serwer o stan i wykryj koniec okna walki. */
  private async syncFightState(): Promise<void> {
    if (this.dead || this.combatPhase !== 'fighting' || this.fightSyncInFlight) return;
    // Gdy pety atakują, `/boss/hit` już syncuje stan — nie marnuj GET-a.
    if (this.pets.some((p) => p.state === 'ATTACKING')) return;
    this.fightSyncInFlight = true;
    try {
      const view = await bossApi.getState();
      if (this.dead || this.combatPhase !== 'fighting') return;
      if (view.state === 'awake') {
        // Wciąż walka — odśwież autorytatywne HP/tarczę (pety stoją, więc bez tego pasek nie żyje).
        this.hpMax = BigInt(view.hpMax);
        this.hpRemaining = BigInt(view.hpRemaining);
        this.shielded = view.shielded;
        this.phaseHpRemaining = BigInt(view.phaseHpRemaining);
        this.awakenedUntil = view.awakenedUntil;
        if (this.hpBarBg) this.updateHpBar();
      } else {
        // Okno walki minęło (lazy-reconcile serwera) → przejdź w cooldown.
        this.stopHitLoop();
        this.stopBossActions();
        this.combatPhase = 'cooling';
        this.showCooling(view.nextAvailableAt);
      }
    } catch {
      // sieć/429 — spróbujemy w kolejnym heartbeacie.
    } finally {
      this.fightSyncInFlight = false;
    }
  }

  /** Planuje kolejną akcję bossa; interwał maleje z HP (enrage: pełne ~2,6 s → niskie ~1,2 s). */
  private scheduleNextBossAction(): void {
    if (this.dead || this.combatPhase !== 'fighting') return;
    const frac = this.fractionOf(this.hpRemaining, this.hpMax);
    const delay = Phaser.Math.Linear(1200, 2600, frac);
    this.bossActionTimer = this.time.delayedCall(delay, () => {
      this.bossAction();
      this.scheduleNextBossAction();
    });
  }

  /** Losowa akcja kolosa (pula z wagami) — beam/slam częstsze, reszta urozmaica. */
  private bossAction(): void {
    if (this.dead || this.combatPhase !== 'fighting' || !this.boss || !this.boss.active) return;
    const roll = Math.random();
    if (roll < 0.24) this.bossBeam();
    else if (roll < 0.46) this.bossSlam();
    else if (roll < 0.64) this.bossWhirlpool();
    else if (roll < 0.80) this.bossSweep();
    else if (roll < 0.92) this.bossBubbles();
    else this.bossDebris();
  }

  /** Wiązka energii z trójzębu w stronę gracza (telegraf → strzał → błysk + wstrząs). */
  private bossBeam(): void {
    const boss = this.boss;
    const player = this.player;
    if (!boss || !player) return;
    const tipX = boss.x + this.bossSize * 0.22;
    const tipY = boss.y - this.bossSize * 0.5;
    this.showEmote('anger');
    // Telegraf: ładowanie na czubku trójzębu.
    const charge = this.add
      .circle(tipX, tipY, this.bossSize * 0.05, 0x9ff0ff, 0.9)
      .setDepth(11)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({ targets: charge, scale: 2.4, alpha: 0, duration: 300, onComplete: () => charge.destroy() });
    this.time.delayedCall(280, () => {
      // Guard combatPhase (LOW review): bez tego wiązka mogłaby wystrzelić w trakcie
      // defeat/cooling/pending/error (akcje zatrzymane, ale ten delayedCall już zaplanowany).
      if (this.dead || this.combatPhase !== 'fighting' || !boss.active || !player) return;
      const px = player.x;
      const py = player.y - this.bossSize * 0.06;
      const g = this.add.graphics().setDepth(11).setBlendMode(Phaser.BlendModes.ADD);
      g.lineStyle(Math.max(8, this.bossSize * 0.03), 0x6fe6ff, 0.95);
      g.lineBetween(tipX, tipY, px, py);
      g.lineStyle(Math.max(3, this.bossSize * 0.012), 0xffffff, 1);
      g.lineBetween(tipX, tipY, px, py);
      this.cameras.main.shake(220, 0.012);
      const burst = this.add
        .circle(px, py, this.bossSize * 0.06, 0xbff4ff, 0.85)
        .setDepth(11)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({ targets: burst, scale: 2.2, alpha: 0, duration: 320, onComplete: () => burst.destroy() });
      this.tweens.add({ targets: g, alpha: 0, duration: 260, onComplete: () => g.destroy() });
    });
  }

  /** Fala uderzeniowa z podstawy kolosa: pierścień + roztrącenie petów w zasięgu. */
  private bossSlam(): void {
    const boss = this.boss;
    if (!boss) return;
    const ox = boss.x;
    const oy = boss.y + this.bossSize * 0.04;
    this.showEmote('resolve');
    const ring = this.add
      .circle(ox, oy, this.bossSize * 0.1, 0xffd27a, 0)
      .setDepth(8)
      .setStrokeStyle(6, 0xffd27a, 0.9)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({ targets: ring, scale: 4.2, alpha: 0, duration: 500, ease: 'Cubic.easeOut', onComplete: () => ring.destroy() });
    this.cameras.main.shake(320, 0.016);
    const radius = this.bossSize * 0.75;
    for (const pet of this.pets) {
      const d = Math.hypot(pet.sprite.x - ox, pet.sprite.y - oy);
      if (d <= radius) pet.knockback(ox, oy, this.bossSize * 0.4);
    }
  }

  /** Wir: animowany swirl u podstawy → wsysa pety do środka, po chwili wyrzuca. */
  private bossWhirlpool(): void {
    const boss = this.boss;
    if (!boss) return;
    const cx = boss.x;
    const cy = boss.y - this.bossSize * 0.12;
    this.showEmote('resolve');
    let swirl: Phaser.GameObjects.Sprite | Phaser.GameObjects.Image | undefined;
    if (this.textures.exists('fx_atlantis_boss_swirl_sheet') && this.anims.exists('fx_atlantis_boss_swirl_anim')) {
      const sp = this.add
        .sprite(cx, cy, 'fx_atlantis_boss_swirl_sheet')
        .setDepth(8)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDisplaySize(this.bossSize * 0.9, this.bossSize * 0.9)
        .setAlpha(0.85);
      sp.play('fx_atlantis_boss_swirl_anim');
      swirl = sp;
    } else if (this.textures.exists('fx_atlantis_boss_swirl')) {
      swirl = this.add
        .image(cx, cy, 'fx_atlantis_boss_swirl')
        .setDepth(8)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setDisplaySize(this.bossSize * 0.8, this.bossSize * 0.8)
        .setAlpha(0.85);
    }
    if (swirl) this.tweens.add({ targets: swirl, angle: 220, alpha: 0, duration: 780, onComplete: () => swirl!.destroy() });
    for (const pet of this.pets) {
      if (pet.sprite.active) pet.pull(cx, cy, this.bossSize * 0.28);
    }
    this.time.delayedCall(620, () => {
      if (this.dead || this.combatPhase !== 'fighting') return;
      this.cameras.main.shake(260, 0.012);
      for (const pet of this.pets) {
        if (pet.sprite.active) pet.knockback(cx, cy, this.bossSize * 0.45);
      }
    });
  }

  /** Zamiatanie trójzębem: telegraf łuku → mocny odrzut petów. */
  private bossSweep(): void {
    const boss = this.boss;
    if (!boss) return;
    const cx = boss.x;
    const cy = boss.y - this.bossSize * 0.18;
    this.showEmote('anger');
    const arc = this.add
      .arc(cx, cy, this.bossSize * 0.62, 200, 340, false, 0x6fe6ff, 0.25)
      .setDepth(8)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({ targets: arc, alpha: 0, scaleX: 1.2, scaleY: 1.2, duration: 320, onComplete: () => arc.destroy() });
    this.time.delayedCall(300, () => {
      if (this.dead || this.combatPhase !== 'fighting' || !boss.active) return;
      this.cameras.main.shake(300, 0.016);
      const streak = this.add
        .arc(cx, cy, this.bossSize * 0.72, 200, 340, false, 0xffffff, 0.5)
        .setDepth(11)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({ targets: streak, alpha: 0, duration: 220, onComplete: () => streak.destroy() });
      for (const pet of this.pets) {
        if (pet.sprite.active) pet.knockback(cx, cy, this.bossSize * 0.5);
      }
    });
  }

  /** Grad bąbli: pociski lecące w stronę gracza, pękają na miejscu (do uniku). */
  private bossBubbles(): void {
    const boss = this.boss;
    const player = this.player;
    if (!boss || !player) return;
    this.showEmote('surprise');
    const hasTex = this.textures.exists('fx_atlantis_bubble');
    const tx = player.x;
    const ty = player.y - this.bossSize * 0.06;
    for (let i = 0; i < 5; i++) {
      const sx = boss.x + Phaser.Math.Between(-this.bossSize * 0.2, this.bossSize * 0.2);
      const sy = boss.y - this.bossSize * 0.45;
      const size = this.bossSize * Phaser.Math.FloatBetween(0.07, 0.12);
      const bubble = hasTex
        ? this.add.image(sx, sy, 'fx_atlantis_bubble').setDepth(11).setDisplaySize(size, size).setAlpha(0.9)
        : this.add.circle(sx, sy, size / 2, 0x9ff0ff, 0.7).setDepth(11).setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({
        targets: bubble,
        x: tx + Phaser.Math.Between(-30, 30),
        y: ty + Phaser.Math.Between(-20, 20),
        duration: 620,
        delay: i * 90,
        ease: 'Sine.easeIn',
        onComplete: () => {
          if (this.dead || this.combatPhase !== 'fighting' || !bubble.active) {
            bubble.destroy();
            return;
          }
          const pop = this.add
            .circle(bubble.x, bubble.y, size * 0.4, 0xbff4ff, 0.8)
            .setDepth(11)
            .setBlendMode(Phaser.BlendModes.ADD);
          this.tweens.add({ targets: pop, scale: 1.8, alpha: 0, duration: 220, onComplete: () => pop.destroy() });
          bubble.destroy();
        },
      });
    }
  }

  /** Spadające gruzy: cienie-ostrzeżenia na podłodze → uderzenie + strefa odrzutu. */
  private bossDebris(): void {
    const { width: w, height: h } = this.scale;
    this.showEmote('anger');
    const r = this.bossSize * 0.18;
    for (let i = 0; i < 3; i++) {
      const x = Phaser.Math.Between(Math.round(w * 0.2), Math.round(w * 0.8));
      const y = Phaser.Math.Between(Math.round(h * 0.55), Math.round(h * 0.9));
      const shadow = this.add.ellipse(x, y, r * 2, r, 0x06141a, 0.4).setDepth(6);
      this.tweens.add({ targets: shadow, scaleX: 1.15, scaleY: 1.15, yoyo: true, repeat: 2, duration: 150 });
      this.time.delayedCall(520 + i * 80, () => {
        if (this.dead || this.combatPhase !== 'fighting') {
          shadow.destroy();
          return;
        }
        shadow.destroy();
        this.cameras.main.shake(180, 0.01);
        const ring = this.add
          .circle(x, y, r * 0.6, 0xc9a24a, 0)
          .setDepth(8)
          .setStrokeStyle(5, 0xe0c074, 0.9)
          .setAlpha(0.9);
        this.tweens.add({ targets: ring, scale: 2.2, alpha: 0, duration: 360, onComplete: () => ring.destroy() });
        for (const pet of this.pets) {
          if (pet.sprite.active && Math.hypot(pet.sprite.x - x, pet.sprite.y - y) <= r * 1.4) {
            pet.knockback(x, y, this.bossSize * 0.35);
          }
        }
      });
    }
  }

  /** Emotka-znaczek (PNG, manga) nad głową kolosa — pop in/out. */
  private showEmote(kind: 'anger' | 'surprise' | 'resolve'): void {
    const boss = this.boss;
    const key = `fx_atlantis_boss_emote_${kind}`;
    if (!boss || !this.textures.exists(key)) return;
    const size = this.bossSize * 0.3;
    const ex = boss.x + this.bossSize * 0.34;
    const ey = boss.y - this.bossSize * 0.62;
    const emote = this.add
      .image(ex, ey + 10, key)
      .setDepth(115)
      .setDisplaySize(size, size)
      .setAlpha(0);
    this.tweens.add({ targets: emote, alpha: 1, y: ey, duration: 160, ease: 'Back.easeOut' });
    this.time.delayedCall(950, () => {
      if (!emote.active) return;
      this.tweens.add({ targets: emote, alpha: 0, y: ey - 14, duration: 260, onComplete: () => emote.destroy() });
    });
  }

  private startHitLoop(): void {
    this.stopHitLoop();
    this.hitTimer = this.time.addEvent({
      delay: HIT_INTERVAL_MS,
      loop: true,
      callback: () => void this.doHit(),
    });
  }

  private stopHitLoop(): void {
    this.hitTimer?.remove();
    this.hitTimer = undefined;
  }

  /** Jedno uderzenie: raport do serwera → autorytatywne HP → render. */
  private async doHit(): Promise<void> {
    if (this.dead || this.combatPhase !== 'fighting' || this.hitInFlight) return;
    // Serwerowe obrażenia płyną tylko, gdy pety REALNIE atakują (boss/posąg) —
    // walka jest „przyczynowa": odejdź od kolosa → pety przestają, HP nie spada.
    if (this.pets.length > 0 && !this.pets.some((p) => p.state === 'ATTACKING')) return;
    this.hitInFlight = true;
    const hitId = this.makeHitId();
    try {
      const res = await runEconomyAction(() => bossApi.hit(REPORTED_DAMAGE, hitId));
      if (this.dead) return;
      const wasShielded = this.shielded;
      this.hpMax = BigInt(res.hpMax);
      this.hpRemaining = BigInt(res.hpRemaining);
      this.shielded = res.shielded;
      this.phaseHpRemaining = BigInt(res.phaseHpRemaining);
      this.awakenedUntil = res.awakenedUntil;

      // Przejścia fazy (serwer autorytatywnie): nowa tarcza ↔ zdjęta tarcza.
      if (res.phase != null) {
        this.onPhaseEnter(res.phase);
      } else if (wasShielded && !res.shielded && !res.defeated) {
        this.onShieldDrop();
      }

      if (!this.hpBarBg) this.buildHpBar();
      this.updateHpBar();
      this.updateHelperStatues();
      this.flashBossHit();
      if (res.defeated) this.onDefeated(res);
    } catch (err) {
      if (this.dead) return;
      this.handleHitError(err);
    } finally {
      this.hitInFlight = false;
    }
  }

  private makeHitId(): string {
    const c = globalThis.crypto;
    if (c && typeof c.randomUUID === 'function') return c.randomUUID();
    return `${Date.now()}-${Math.floor(Math.random() * 1e9)}`;
  }

  private handleHitError(err: unknown): void {
    if (err instanceof ApiError) {
      if (err.code === 'dormant_cooling') {
        // Race: okno awake minęło w trakcie walki — boss zasnął.
        this.stopHitLoop();
        this.stopBossActions();
        this.combatPhase = 'cooling';
        void this.refreshCooling();
        return;
      }
      if (err.code === 'pending_reward_unclaimed') {
        this.stopHitLoop();
        this.stopBossActions();
        this.combatPhase = 'pending';
        void this.tryClaimPending(true);
        return;
      }
      if (err.code === 'make_room') {
        // Pełne inventory = terminalny stan wejścia: zatrzymaj pętlę, wyjdź z 'fighting',
        // inaczej timer spamuje /boss/hit co 700 ms i generuje kolejne 409.
        this.stopHitLoop();
        this.stopBossActions();
        this.combatPhase = 'error';
        this.showStatus('Zrób miejsce w ekwipunku na nagrodę Strażnika,\npotem wejdź ponownie.');
        return;
      }
      if (err.code === ECONOMY_RESYNC_CODE) {
        // Preflight zablokował mutację, bo gra nie zna swojego stanu. Ten błąd
        // NIE mija sam z kolejnym tickiem — a pętla bije co 700 ms, więc bez
        // zatrzymania wysyłałaby `GET /saves/me` w kółko, bez komunikatu
        // (Codex V-4 FIXES⁵ MED 1 — regresja po wprowadzeniu preflightu).
        this.stopHitLoop();
        this.stopBossActions();
        this.combatPhase = 'error';
        this.showStatus(
          ['Gra straciła kontakt z zapisem.', 'Wyjdź i wejdź ponownie za chwilę —', 'nic nie przepadło.'].join(
            '\n',
          ),
        );
        return;
      }
      // 429 rate-limit / 0 network → pomiń tick, kolejny spróbuje.
    }
  }

  /** Sekwencja pokonania: zatrzymaj pętlę, kino, panel nagrody. */
  private onDefeated(res: BossHitResponse): void {
    this.combatPhase = 'defeated';
    this.stopHitLoop();
    this.stopBossActions();
    this.hpRemaining = 0n;
    this.updateHpBar();

    // Boss nietykalny + sprzątnięcie tarczy/posągów; pety przejdą w idle-follow.
    this.setBossTargetable(false);
    this.targeting?.stop();
    this.showBossAura(false);
    this.clearHelperStatues(true);
    this.clearBossPortal(); // zgaś trwały wir za bossem — znika razem z kolosem

    this.showTaunt(TAUNTS.defeat);
    this.playDefeatFinale(res);
  }

  // ══ Fala 3 (§10): epicki finał — rozpad kolosa + złoto + slow-mo + kino ════
  // Czysto wizualne: HP/nagroda już rozstrzygnięte serwerowo (`res`). Slow-mo tylko
  // na `tweens.timeScale` — kamera i `delayedCall` lecą realnym czasem (timing panelu
  // nagrody bez zmian). `tweens.timeScale` resetowany defensywnie w SHUTDOWN/reset.

  /** Pomocnik: zaplanuj „beat" kina (real-time, z guardem `!dead`). */
  private beat(ms: number, fn: () => void): void {
    this.time.delayedCall(ms, () => {
      if (!this.dead) fn();
    });
  }

  /**
   * Anime-kino śmierci Strażnika (~23 s, wielofazowe). Czysto wizualne:
   * HP/nagroda już rozstrzygnięte serwerowo (`res`). Fazy:
   *   1 (0–3 s)  słabnięcie — boss się chwieje, błyski, powolny zoom-in,
   *   2 (3–8 s)  pękająca skorupa — świetliste szczeliny + obrotowe promienie,
   *   3 (8–13 s) przeciążenie — biały puls, fale promieni, narastający wstrząs,
   *   4 (13 s)   KLIMAKS — wielki błysk, deep slow-mo, słup światła, rozpad kolosa,
   *   5 (14–21 s) deszcz złota — długa eksplozja + spadające monety,
   *   6 (21–23,5 s) wyciszenie kamery → panel nagrody.
   */
  private playDefeatFinale(res: BossHitResponse): void {
    const cam = this.cameras.main;
    const boss = this.boss;
    if (boss) {
      this.tweens.killTweensOf(boss); // przejmujemy bossa — stop „oddechu"/idle
      boss.off(Phaser.Animations.Events.ANIMATION_COMPLETE);
      boss.stop();
    }
    cam.flash(220, 255, 250, 230);

    // ── FAZA 1: słabnięcie ────────────────────────────────────────────────
    this.finaleBanner('Strażnik słabnie...');
    cam.zoomTo(1.12, 2600, 'Sine.easeInOut');
    if (boss) {
      this.tweens.add({
        targets: boss,
        angle: { from: -4, to: 4 },
        duration: 420,
        yoyo: true,
        repeat: 5,
        ease: 'Sine.easeInOut',
      });
    }
    for (let i = 0; i < 5; i++) {
      this.beat(300 + i * 520, () => {
        this.bossSpark();
        cam.shake(180, 0.006);
      });
    }

    // ── FAZA 2: pękająca skorupa + wyciek światła ─────────────────────────
    this.beat(3000, () => {
      this.finaleBanner('Pęka orichalkowa skorupa!');
      cam.zoomTo(1.22, 3200, 'Sine.easeInOut');
      this.spawnCracks();
      this.spawnLightRays();
    });
    for (let i = 0; i < 6; i++) {
      this.beat(3600 + i * 700, () => {
        this.bossSurfaceBurst();
        cam.shake(160, 0.008);
      });
    }

    // ── FAZA 3: przeciążenie ──────────────────────────────────────────────
    this.beat(8000, () => {
      this.finaleBanner('NIE… MOJA ŚWIĄTYNIA!');
      cam.zoomTo(1.3, 3000, 'Sine.easeInOut');
    });
    for (let i = 0; i < 5; i++) {
      this.beat(8200 + i * 950, () => {
        this.radiateBeams();
        this.bossWhiteFlash();
        cam.shake(320, 0.012 + i * 0.002);
      });
    }

    // ── FAZA 4: KLIMAKS / rozpad ──────────────────────────────────────────
    this.beat(13000, () => {
      cam.flash(800, 255, 252, 235);
      this.lightPillar();
      this.fadeFinaleFx(); // zgaś pęknięcia/promienie przed rozpadem
      this.tweens.timeScale = 0.25; // deep slow-mo na sam rozpad
      this.shatterBoss();
      this.finaleBanner('STRAŻNIK POKONANY!');
    });
    this.beat(16800, () => {
      this.tweens.timeScale = 1;
    });

    // ── FAZA 5: deszcz złota ──────────────────────────────────────────────
    this.beat(14200, () => this.goldExplosion());

    // ── FAZA 6: wyciszenie + panel ────────────────────────────────────────
    this.beat(21000, () => cam.zoomTo(1, 1800, 'Sine.easeInOut'));
    this.beat(23500, () => this.showRewardPanel(res));
  }

  /** Wielki centralny banner fazy (anime), slide-in + hold + fade. */
  private finaleBanner(text: string): void {
    const { width: w, height: h } = this.scale;
    const t = this.add
      .text(w / 2, h * 0.3, text, {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(Math.min(w, h) * 0.055)}px`,
        color: '#fff2d6',
        fontStyle: 'bold',
        stroke: '#3a0e06',
        strokeThickness: Math.max(3, Math.round(Math.min(w, h) * 0.01)),
        align: 'center',
      })
      .setOrigin(0.5)
      .setDepth(150)
      .setScrollFactor(0)
      .setAlpha(0)
      .setShadow(0, 2, 'rgba(0,0,0,0.75)', 6);
    this.tweens.add({
      targets: t,
      alpha: 1,
      y: t.y - 10,
      duration: 320,
      yoyo: true,
      hold: 1500,
      ease: 'Sine.easeInOut',
      onComplete: () => t.destroy(),
    });
  }

  /** Złota iskra przy powierzchni kolosa. */
  private bossSpark(): void {
    const b = this.boss;
    if (!b) return;
    const x = b.x + Phaser.Math.Between(-this.bossSize * 0.3, this.bossSize * 0.3);
    const y = b.y - this.bossSize * 0.4 + Phaser.Math.Between(-this.bossSize * 0.2, this.bossSize * 0.2);
    spawnSparkleBurst(this, x, y, 'gold');
  }

  /** Wybuch gruzu + świetlisty rozbłysk z pęknięcia na powierzchni kolosa. */
  private bossSurfaceBurst(): void {
    const b = this.boss;
    if (!b) return;
    const x = b.x + Phaser.Math.Between(-this.bossSize * 0.28, this.bossSize * 0.28);
    const y = b.y - this.bossSize * 0.4 + Phaser.Math.Between(-this.bossSize * 0.25, this.bossSize * 0.2);
    spawnDestructionFX(this, x, y, 'stone');
    const glow = this.add
      .circle(x, y, this.bossSize * 0.06, 0xfff0b0, 0.9)
      .setDepth(12)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({ targets: glow, scale: 2.4, alpha: 0, duration: 600, onComplete: () => glow.destroy() });
  }

  /** Narastające świetliste pęknięcia rozchodzące się od środka kolosa. */
  private spawnCracks(): void {
    const b = this.boss;
    if (!b) return;
    const cx = b.x;
    const cy = b.y - this.bossSize * 0.4;
    const g = this.add.graphics().setDepth(12).setBlendMode(Phaser.BlendModes.ADD);
    this.finaleFx.push(g);
    const draw = (progress: number): void => {
      g.clear();
      const rng = new Phaser.Math.RandomDataGenerator(['guardian-cracks']);
      g.lineStyle(3, 0xffe79a, 0.9);
      for (let k = 0; k < 8; k++) {
        const baseAng = (k / 8) * Math.PI * 2 + rng.frac() * 0.6;
        const len = this.bossSize * 0.44 * progress;
        let x = cx;
        let y = cy;
        g.beginPath();
        g.moveTo(x, y);
        const steps = 5;
        for (let s = 1; s <= steps; s++) {
          const a = baseAng + (rng.frac() - 0.5) * 0.5;
          x += Math.cos(a) * (len / steps);
          y += Math.sin(a) * (len / steps);
          g.lineTo(x, y);
        }
        g.strokePath();
      }
    };
    const obj = { p: 0 };
    this.tweens.add({ targets: obj, p: 1, duration: 3200, ease: 'Quad.easeIn', onUpdate: () => draw(obj.p) });
  }

  /** Obrotowy „sunburst" promieni za kolosem (rośnie + wiruje). */
  private spawnLightRays(): void {
    const b = this.boss;
    if (!b) return;
    const container = this.add.container(b.x, b.y - this.bossSize * 0.35).setDepth(8);
    this.finaleFx.push(container);
    const N = 12;
    for (let i = 0; i < N; i++) {
      const tri = this.add
        .triangle(0, 0, 0, 0, -10, -this.bossSize * 0.95, 10, -this.bossSize * 0.95, 0xfff0c0, 0.16)
        .setBlendMode(Phaser.BlendModes.ADD);
      tri.setAngle((i / N) * 360);
      container.add(tri);
    }
    container.setScale(0.2);
    this.tweens.add({ targets: container, scale: 1, duration: 3500, ease: 'Sine.easeOut' });
    this.tweens.add({ targets: container, angle: 360, duration: 12000, repeat: -1, ease: 'Linear' });
  }

  /** Fala promieni wystrzeliwujących na zewnątrz od kolosa. */
  private radiateBeams(): void {
    const b = this.boss;
    if (!b) return;
    const cx = b.x;
    const cy = b.y - this.bossSize * 0.4;
    const N = 8;
    for (let i = 0; i < N; i++) {
      const ang = (i / N) * Math.PI * 2 + Phaser.Math.FloatBetween(-0.2, 0.2);
      const beam = this.add
        .rectangle(cx, cy, this.bossSize * 0.9, 6, 0xfff2c0, 0.8)
        .setOrigin(0, 0.5)
        .setDepth(13)
        .setBlendMode(Phaser.BlendModes.ADD);
      beam.setRotation(ang);
      beam.scaleX = 0.1;
      this.tweens.add({
        targets: beam,
        scaleX: 1.6,
        alpha: 0,
        duration: 520,
        ease: 'Quad.easeOut',
        onComplete: () => beam.destroy(),
      });
    }
  }

  /** Krótki biały rozbłysk całego kolosa (przeciążenie). */
  private bossWhiteFlash(): void {
    const b = this.boss;
    if (!b || !b.active) return;
    b.setTintFill(0xffffff);
    this.time.delayedCall(90, () => {
      if (b.active) b.clearTint();
    });
  }

  /** Pionowy słup światła z miejsca kolosa (moment rozpadu). */
  private lightPillar(): void {
    const { width: w, height: h } = this.scale;
    const x = this.boss ? this.boss.x : w / 2;
    const pillar = this.add
      .rectangle(x, h, this.bossSize * 0.5, h, 0xfff4d0, 0)
      .setOrigin(0.5, 1)
      .setDepth(9)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.tweens.add({
      targets: pillar,
      alpha: 0.85,
      duration: 250,
      yoyo: true,
      hold: 600,
      onComplete: () => {
        this.tweens.add({
          targets: pillar,
          alpha: 0,
          scaleX: 2,
          duration: 900,
          onComplete: () => pillar.destroy(),
        });
      },
    });
  }

  /** Gaszenie trwałych dekoracji kina (pęknięcia/promienie) przed rozpadem. */
  private fadeFinaleFx(): void {
    const fx = this.finaleFx;
    this.finaleFx = [];
    for (const o of fx) {
      const go = o as Phaser.GameObjects.GameObject & { destroy: () => void };
      this.tweens.killTweensOf(o);
      this.tweens.add({ targets: o, alpha: 0, duration: 500, onComplete: () => go.destroy() });
    }
  }

  /** Roztrzaskanie sprite'a bossa na siatkę kawałków (reuse bieżącej klatki + `setCrop`). */
  private shatterBoss(): void {
    const boss = this.boss;
    if (!boss) return;
    boss.off(Phaser.Animations.Events.ANIMATION_COMPLETE); // stop pętli idle A/B
    boss.stop();

    const texKey = boss.texture.key;
    const frameName = boss.frame.name;
    const fw = boss.frame.realWidth;
    const fh = boss.frame.realHeight;
    const cols = 3;
    const rows = 3;
    const cw = fw / cols;
    const ch = fh / rows;
    const dustY = boss.y - this.bossSize * 0.4; // środek masy (origin 0.85 → wyżej)

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const shard = this.add
          .sprite(boss.x, boss.y, texKey, frameName)
          .setOrigin(boss.originX, boss.originY)
          .setDepth(boss.depth)
          .setDisplaySize(this.bossSize, this.bossSize)
          .setFlipX(boss.flipX);
        shard.setCrop(c * cw, r * ch, cw, ch);

        const dirX = c - (cols - 1) / 2 || Phaser.Math.FloatBetween(-0.4, 0.4);
        const dirY = r - (rows - 1) / 2 - 0.5; // bias do góry przy rozrzucie
        const dist = this.bossSize * Phaser.Math.FloatBetween(0.25, 0.55);
        // Rozrzut → zawisają chwilę (slow-mo) → opadanie z obrotem i zanikiem.
        this.tweens.add({
          targets: shard,
          x: shard.x + dirX * dist,
          y: shard.y + dirY * dist * 0.5,
          duration: 550,
          ease: 'Quad.easeOut',
          onComplete: () => {
            if (!shard.active) return;
            this.tweens.add({
              targets: shard,
              y: shard.y + this.bossSize * 1.15,
              angle: Phaser.Math.Between(-160, 160),
              alpha: 0,
              duration: 2800,
              ease: 'Quad.easeIn',
              onComplete: () => shard.destroy(),
            });
          },
        });
      }
    }
    // Kurz w paru punktach u podstawy.
    spawnDestructionFX(this, boss.x, dustY, 'stone');
    spawnDestructionFX(this, boss.x - this.bossSize * 0.2, dustY + this.bossSize * 0.15, 'stone');
    spawnDestructionFX(this, boss.x + this.bossSize * 0.2, dustY + this.bossSize * 0.15, 'stone');
    boss.setVisible(false);
  }

  /** Eksplozja złota: pełnoekranowy błysk + wybuch w miejscu kolosa + deszcz monet. */
  private goldExplosion(): void {
    const { width: w } = this.scale;
    const boss = this.boss;
    const bx = boss ? boss.x : w / 2;
    const by = boss ? boss.y - this.bossSize * 0.4 : this.scale.height * 0.45;

    // Wielki błysk + seria wybuchów w miejscu kolosa (sustained, nie jeden puff).
    this.cameras.main.flash(640, 255, 248, 220);
    for (let i = 0; i < 7; i++) {
      this.time.delayedCall(i * 380, () => {
        if (this.dead) return;
        const ox = Phaser.Math.Between(-this.bossSize * 0.25, this.bossSize * 0.25);
        const oy = Phaser.Math.Between(-this.bossSize * 0.2, this.bossSize * 0.1);
        spawnDestructionFX(this, bx + ox, by + oy, 'coin');
        spawnSparkleBurst(this, bx + ox, by + oy, 'gold');
      });
    }

    // Długi deszcz monet z górnej krawędzi (~7 s, wybuchy z grawitacją = spadające złoto).
    for (let i = 0; i < 34; i++) {
      const rx = Phaser.Math.Between(Math.round(w * 0.12), Math.round(w * 0.88));
      this.time.delayedCall(i * 200, () => {
        if (this.dead) return;
        spawnDestructionFX(this, rx, -10, 'coin');
      });
    }
  }

  /**
   * DEV-only podgląd finału (Fala 3) BEZ walki/serwera — do obejrzenia animacji
   * pokonania w arenie (kolos istnieje od wejścia). Woła `__dev.bossFinale()`.
   * Nic nie wypłaca: mock `reward` służy tylko panelowi nagrody.
   */
  devPreviewFinale(): void {
    if (!import.meta.env.DEV || !this.boss) return;
    const mock: BossHitResponse = {
      state: 'awake',
      hpRemaining: '0',
      hpMax: '1000',
      awakenedUntil: null,
      nextAvailableAt: null,
      totalDamage: '0',
      firstClearDone: true,
      shielded: false,
      phaseHpRemaining: '0',
      phaseIndex: 0,
      pendingReward: null,
      applied: '0',
      defeated: true,
      phase: null,
      reward: {
        coins: '90000000000',
        gems: 30,
        royalEggId: 'egg-atlantis-royal',
        royalEggs: 4,
        rulerPetId: null,
        firstClear: true,
        title: null,
      },
      firstClear: true,
      save_rev: 0,
    };
    this.combatPhase = 'fighting'; // by przejść w 'defeated' z aktywnego stanu
    this.onDefeated(mock);
  }

  /**
   * DEV-only podgląd KINA WEJŚCIA (przebudzenie + portal) bez serwera/walki.
   * Resetuje kolosa do statuy i odgrywa sekwencję. Woła `__dev.bossAwaken()`.
   */
  devPreviewAwaken(): void {
    if (!import.meta.env.DEV || !this.boss) return;
    this.tweens.killTweensOf(this.boss);
    this.clearBossPortal();
    const dormantKey = this.textures.exists('boss_atlantis_guardian')
      ? 'boss_atlantis_guardian'
      : 'placeholder-square';
    this.boss
      .setVisible(true)
      .setAlpha(1)
      .clearTint()
      .setAngle(0)
      .stop()
      .setTexture(dormantKey)
      .setDisplaySize(this.bossSize, this.bossSize);
    this.combatPhase = 'fighting';
    this.playAwakenCinematic(() => {});
  }

  // ══ ATL-2b.C: fazy, tarcza, posągi-pomocnicy ═════════════════════════════
  // Serwer egzekwuje pulę HP fazy (`phase_hp_remaining`); klient WIZUALIZUJE:
  // boss podnosi tarczę → aura + posągi → pety przełączają się na posągi →
  // pula drenuje (posągi się kruszą) → tarcza spada → pety wracają na bossa.

  /** Boss podniósł tarczę (serwer: `res.phase` = numer fazy). Kino + posągi. */
  private onPhaseEnter(phase: number): void {
    // Pula zaraz po podniesieniu = 100% paska tarczy (dla frakcji posągów/paska).
    this.phaseHpMax = this.phaseHpRemaining > 0n ? this.phaseHpRemaining : 1n;
    this.cameras.main.flash(260, 79, 214, 224); // cyjanowy błysk „tarcza w górę"
    this.cameras.main.shake(360, 0.012);
    this.zoomPunch();
    this.enterShieldVisuals();
    this.showPhaseBanner(phase);
    const lines = TAUNTS.phase;
    this.showTaunt(lines[(phase - 1) % lines.length] ?? lines[0]!);
  }

  /** Wielki centralny banner wejścia w fazę (slide+fade) — eskalacja stawki. */
  private showPhaseBanner(phase: number): void {
    const { width: w, height: h } = this.scale;
    const texts = ['FAZA II — STRAŻNIK SIĘ WŚCIEKA!', 'FAZA III — OSTATNIE TCHNIENIE!'];
    const msg = texts[(phase - 1) % texts.length] ?? texts[0]!;
    const banner = this.add
      .text(w / 2, h * 0.24, msg, {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(Math.min(w, h) * 0.055)}px`,
        color: '#f4d27a',
        fontStyle: 'bold',
        align: 'center',
        stroke: '#3a0e06',
        strokeThickness: Math.max(4, Math.round(Math.min(w, h) * 0.012)),
        backgroundColor: 'rgba(8,20,26,0.78)', // gwarantowany kontrast nad złotym kolosem
        padding: { x: 22, y: 12 },
      })
      .setOrigin(0.5)
      .setDepth(140)
      .setScrollFactor(0)
      .setShadow(0, 3, 'rgba(0,0,0,0.8)', 6)
      .setAlpha(0)
      .setScale(0.7);
    this.tweens.add({ targets: banner, alpha: 1, scale: 1, duration: 280, ease: 'Back.easeOut' });
    this.time.delayedCall(1500, () => {
      if (!banner.active) return;
      this.tweens.add({ targets: banner, alpha: 0, y: banner.y - 24, duration: 360, onComplete: () => banner.destroy() });
    });
  }

  /**
   * „DO BOJU!" — wizualny zryw drużyny (React przycisk → `sanctum:rally`).
   * Synchroniczny wypad petów + iskry + pierścienie + błysk/kino. **Czysto efektowne:**
   * NIE dotyka HP (serwer clampuje tempo) — to feel-good moment, nie bonus do obrażeń.
   * Cooldown ~3 s (guard sceny + lustrzany w UI przycisku) — spamowalne.
   */
  private rally(): void {
    const now = this.time.now;
    if (this.dead || this.combatPhase !== 'fighting' || now < this.rallyReadyAt) return;
    this.rallyReadyAt = now + RALLY_COOLDOWN_MS;
    // Potwierdź akceptację → UI startuje cooldown DOPIERO teraz (klik w złym stanie
    // nie zablokuje przycisku — fix LOW review).
    eventBus.emit('sanctum:rallyState', { cooldownMs: RALLY_COOLDOWN_MS });

    this.cameras.main.flash(160, 255, 240, 200);
    this.zoomPunch();
    this.showRallyFlourish();

    const boss = this.boss;
    for (const pet of this.pets) {
      if (!pet.sprite.active) continue;
      // Synchroniczny wypad w stronę kolosa (reuse animatora peta).
      if (boss) {
        const dx = boss.x - pet.sprite.x;
        const dy = boss.y - this.bossSize * 0.3 - pet.sprite.y;
        const len = Math.hypot(dx, dy) || 1;
        pet.animator.triggerAttackHit(dx / len, dy / len);
      }
      spawnSparkleBurst(this, pet.sprite.x, pet.sprite.y, 'gold');
      const ring = this.add
        .circle(pet.sprite.x, pet.sprite.y, 20, 0xffd27a, 0)
        .setDepth(7)
        .setStrokeStyle(3, 0xffe7a6, 0.9)
        .setBlendMode(Phaser.BlendModes.ADD)
        .setAlpha(0.9);
      this.tweens.add({ targets: ring, scale: 2.6, alpha: 0, duration: 420, onComplete: () => ring.destroy() });
    }
  }

  /** Krótki centralny napis „DO BOJU!" przy zagrzaniu drużyny. */
  private showRallyFlourish(): void {
    const { width: w, height: h } = this.scale;
    const t = this.add
      .text(w / 2, h * 0.42, 'DO BOJU!', {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(Math.min(w, h) * 0.05)}px`,
        color: '#fff2d6',
        fontStyle: 'bold',
        stroke: '#3a0e06',
        strokeThickness: Math.max(3, Math.round(Math.min(w, h) * 0.01)),
      })
      .setOrigin(0.5)
      .setDepth(141)
      .setScrollFactor(0)
      .setShadow(0, 2, 'rgba(0,0,0,0.75)', 5)
      .setAlpha(0)
      .setScale(0.6);
    this.tweens.add({
      targets: t,
      alpha: 1,
      scale: 1.1,
      duration: 180,
      ease: 'Back.easeOut',
      onComplete: () => {
        this.tweens.add({ targets: t, alpha: 0, scale: 1.25, y: t.y - 20, delay: 420, duration: 280, onComplete: () => t.destroy() });
      },
    });
  }

  /** Tarcza spadła (pula fazy wyczerpana). Posągi gruz, pety wracają na bossa. */
  private onShieldDrop(): void {
    this.cameras.main.shake(300, 0.01);
    this.exitShieldVisuals();
    this.showEmote('surprise');
    const lines = TAUNTS.shieldDrop;
    this.showTaunt(lines[Math.floor(Math.random() * lines.length)] ?? lines[0]!);
  }

  /** Włącza/wyłącza namierzanie wszystkich kotwic kolosa (tarcza = nietykalny). */
  private setBossTargetable(v: boolean): void {
    for (const a of this.bossAnchors) a.setTargetable(v);
  }

  private enterShieldVisuals(): void {
    this.setBossTargetable(false); // kolos nietykalny → pety puszczają go
    this.spawnHelperStatues(HELPER_STATUES);
    this.showBossAura(true);
  }

  private exitShieldVisuals(): void {
    this.showBossAura(false);
    this.clearHelperStatues(true);
    this.setBossTargetable(true); // kolos znów tykalny → targeting wróci na niego
  }

  /** Spawnuje N posągów (reuse `dest_atlantis_guardian_statue`) jako prawdziwe cele petów. */
  private spawnHelperStatues(count: number): void {
    this.clearHelperStatues();
    const { width: w, height: h } = this.scale;
    const tex = this.textures.exists('dest_atlantis_guardian_statue')
      ? 'dest_atlantis_guardian_statue'
      : 'placeholder-square';
    const size = Math.min(w, h) * 0.16;
    const y = h * 0.7;
    for (let i = 0; i < count; i++) {
      const x = count > 1 ? w * (0.24 + 0.52 * (i / (count - 1))) : w * 0.5;
      const sprite = this.add.image(x, y + size * 0.4, tex).setDepth(7).setDisplaySize(size, size).setAlpha(0);
      this.tweens.add({ targets: sprite, alpha: 1, y, duration: 360, ease: 'Back.easeOut' });
      // Cel nieautorytatywny: pety realnie do niego biegną i biją (efekty z peta).
      const target = new ArenaTarget(() => ({ x: sprite.x, y: sprite.y }), 2);
      target.setTargetable(true);
      this.helperStatues.push({ sprite, target });
      this.targets.push(target);
    }
  }

  /** Repozycja żywych posągów (resize). Cele śledzą sprite przez closure — bez retargetu. */
  private layoutHelperStatues(): void {
    const { width: w, height: h } = this.scale;
    const size = Math.min(w, h) * 0.16;
    const y = h * 0.7;
    const n = this.helperStatues.length;
    this.helperStatues.forEach((entry, i) => {
      if (!entry.sprite.active) return;
      const x = n > 1 ? w * (0.24 + 0.52 * (i / (n - 1))) : w * 0.5;
      entry.sprite.setPosition(x, y).setDisplaySize(size, size);
    });
  }

  /** Kruszenie posągów proporcjonalnie do drenażu serwerowej puli tarczy. */
  private updateHelperStatues(): void {
    if (!this.shielded || this.helperStatues.length === 0) return;
    const frac = this.fractionOf(this.phaseHpRemaining, this.phaseHpMax);
    const remain = Math.max(0, Math.ceil(frac * HELPER_STATUES));
    let crumbled = false;
    while (this.helperStatues.length > remain) {
      const entry = this.helperStatues.pop();
      if (!entry) break;
      entry.target.setTargetable(false); // atakujące pety puszczą → targeting da im żywe posągi
      this.removeTarget(entry.target);
      if (entry.sprite.active) {
        this.crumbleStatue(entry.sprite);
        crumbled = true;
      }
    }
    if (crumbled) this.cameras.main.shake(180, 0.006);
  }

  /** Usuwa posągi (cele + sprite'y): `crumble` = animowany gruz, inaczej natychmiast. */
  private clearHelperStatues(crumble = false): void {
    const statues = this.helperStatues;
    this.helperStatues = [];
    for (const { sprite, target } of statues) {
      target.setTargetable(false);
      this.removeTarget(target);
      if (!sprite.active) continue;
      if (crumble) this.crumbleStatue(sprite);
      else sprite.destroy();
    }
  }

  private removeTarget(t: PetTarget): void {
    const idx = this.targets.indexOf(t);
    if (idx >= 0) this.targets.splice(idx, 1);
  }

  private crumbleStatue(st: Phaser.GameObjects.Image): void {
    this.tweens.killTweensOf(st);
    this.tweens.add({
      targets: st,
      alpha: 0,
      y: st.y + 18,
      angle: Phaser.Math.Between(-25, 25),
      scaleX: st.scaleX * 0.7,
      scaleY: st.scaleY * 0.7,
      duration: 360,
      ease: 'Quad.easeIn',
      onComplete: () => st.destroy(),
    });
  }

  /** Pulsująca aura cyjan za bossem na czas tarczy. */
  private showBossAura(on: boolean): void {
    if (on) {
      if (this.bossAura || !this.boss) return;
      this.bossAura = this.add
        .circle(this.boss.x, this.boss.y - this.bossSize * 0.3, this.bossSize * 0.6, 0x4fd6e0, 0.16)
        .setDepth(9)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.tweens.add({
        targets: this.bossAura,
        alpha: 0.32,
        scale: 1.08,
        duration: 700,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut',
      });
    } else if (this.bossAura) {
      this.tweens.killTweensOf(this.bossAura);
      this.bossAura.destroy();
      this.bossAura = undefined;
    }
  }

  /** Krótki „enrage" zoom-punch kamery (wraca do 1). */
  private zoomPunch(): void {
    const cam = this.cameras.main;
    this.tweens.add({
      targets: cam,
      zoom: 1.05,
      duration: 200,
      yoyo: true,
      ease: 'Sine.easeInOut',
      onComplete: () => cam.setZoom(1),
    });
  }

  /** Panel nagrody (Phaser overlay). Monety/gemy już zaaplikowane przez runEconomyAction. */
  /**
   * Pokazuje ładny modal nagrody — przez React (`BossRewardModal`, event
   * `sanctum:reward`): prawdziwe ikony monet/gemów + grafika Jajka Królewskiego
   * + ewentualny Władca. Zamiast dawnego tekstowego panelu rysowanego w Phaserze.
   * Wartości są już zaksięgowane serwerowo. Zamknięcie modala → `sanctum:exit`.
   */
  private showRewardPanel(res: BossHitResponse): void {
    const r = res.reward;
    eventBus.emit('sanctum:reward', {
      coins: r?.coins ?? '0',
      gems: r?.gems ?? 0,
      royalEggs: r?.royalEggs ?? 0,
      royalEggId: r?.royalEggId ?? null,
      rulerPetId: r?.rulerPetId ?? null,
      firstClear: r?.firstClear ?? false,
      deferred: res.rewardDeferred ?? false,
    });
  }

  /** Boss śpi — licznik do przebudzenia + auto-odświeżanie co sekundę. */
  private showCooling(nextAvailableAt: number | null): void {
    this.cooldownUntil = nextAvailableAt;
    this.updateCoolingLabel();
    this.coolingTimer?.remove();
    this.coolingTimer = this.time.addEvent({
      delay: 1000,
      loop: true,
      callback: () => this.updateCoolingLabel(),
    });
  }

  private updateCoolingLabel(): void {
    if (this.dead) return;
    const ms = this.cooldownUntil ? this.cooldownUntil - Date.now() : 0;
    if (ms <= 0) {
      this.coolingTimer?.remove();
      this.coolingTimer = undefined;
      this.showStatus('Strażnik znów się budzi…\nWyjdź i wejdź ponownie, by stanąć do walki.');
      return;
    }
    this.showStatus(`Strażnik śpi jako posąg.\nPrzebudzi się za ${this.formatCooldown(ms)}.`);
  }

  /** Świeży stan po race „zasnął w trakcie walki". */
  private async refreshCooling(): Promise<void> {
    try {
      const view = await bossApi.getState();
      if (this.dead) return;
      this.showCooling(view.nextAvailableAt);
    } catch {
      if (!this.dead) this.showCooling(null);
    }
  }

  private formatCooldown(ms: number): string {
    const totalSec = Math.ceil(ms / 1000);
    const hh = Math.floor(totalSec / 3600);
    const mm = Math.floor((totalSec % 3600) / 60);
    const ss = totalSec % 60;
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return hh > 0 ? `${hh}:${pad(mm)}:${pad(ss)}` : `${pad(mm)}:${pad(ss)}`;
  }

  /** Komunikat centralny (cooldown / błąd / pending). Tworzy lub aktualizuje label. */
  private showStatus(msg: string): void {
    const { width: w, height: h } = this.scale;
    if (!this.statusLabel) {
      this.statusLabel = this.add
        .text(w / 2, h * 0.5, msg, {
          fontFamily: 'sans-serif',
          fontSize: `${Math.round(Math.min(w, h) * 0.032)}px`,
          color: '#f4ecd8',
          align: 'center',
          backgroundColor: 'rgba(11,31,39,0.85)',
          padding: { x: 18, y: 12 },
        })
        .setOrigin(0.5, 0.5)
        .setDepth(150)
        .setScrollFactor(0);
    } else {
      this.statusLabel.setText(msg);
    }
    this.statusLabel.setVisible(msg.length > 0);
  }

  /** Dymek-taunt nad bossem (PNG-free — sam tekst w ramce). Auto-znika. */
  private showTaunt(text: string): void {
    const boss = this.boss;
    if (!boss) return;
    const y = boss.y - this.bossSize * 0.82;
    const label = this.add
      .text(boss.x, y, text, {
        fontFamily: 'sans-serif',
        fontSize: `${Math.round(this.bossSize * 0.075)}px`,
        color: '#1a0e06',
        align: 'center',
        backgroundColor: 'rgba(244,236,216,0.95)',
        padding: { x: 14, y: 9 },
      })
      .setOrigin(0.5, 1)
      .setDepth(120)
      .setScrollFactor(0)
      .setAlpha(0);
    this.tweens.add({ targets: label, alpha: 1, y: y - 8, duration: 220 });
    this.time.delayedCall(2600, () => {
      if (!label.active) return;
      this.tweens.add({ targets: label, alpha: 0, duration: 400, onComplete: () => label.destroy() });
    });
  }

  /** Próba odbioru nieodebranej nagrody. `noticeOnFail` → komunikat „zrób miejsce". */
  private async tryClaimPending(noticeOnFail: boolean): Promise<void> {
    try {
      const res = await runEconomyAction(() => bossApi.claimPending());
      if (this.dead) return;
      const eggs = res.claimed.royalEggs;
      const ruler = res.claimed.rulerPetId
        ? (PETS_BY_ID[res.claimed.rulerPetId]?.name.pl ?? 'Władca')
        : null;
      const rulerLine = ruler ? `\nWŁADCA: ${ruler}!` : '';
      this.showStatus(`Odebrano nagrodę Strażnika:\n${eggs}× Królewskie Jajko${rulerLine}\nWróć do Atlantydy.`);
    } catch (err) {
      if (this.dead) return;
      if (err instanceof ApiError && err.code === ECONOMY_RESYNC_CODE) {
        // Nagroda CZEKA na serwerze — nie zniknęła, tylko nie umiemy jej teraz
        // odebrać. Komunikat „zrób miejsce" podałby złą przyczynę.
        this.showStatus(
          ['Gra straciła kontakt z zapisem.', 'Nagroda Strażnika czeka —', 'wejdź ponownie za chwilę.'].join('\n'),
        );
        return;
      }
      if (noticeOnFail || (err instanceof ApiError && err.code === 'make_room')) {
        this.showStatus('Masz nieodebraną nagrodę Strażnika.\nZrób miejsce w ekwipunku i wejdź ponownie.');
      }
    }
  }

  private exitToWorld(): void {
    startTeleport(this, { target: 'WorldScene', accent: biomeAccent('biome-atlantis') });
  }
}
